import React, { Component, useRef, useState, useEffect } from "react";
import { Container, Card, Badge } from "react-bootstrap";
import gql from "graphql-tag";
import { withApollo } from "react-apollo";
import * as Cookies from "es-cookie";
import CardLessDataTable from "../datatables/CardLessDataTableWithSearch";
import "../DocumentServices/_documentServices.scss";

const CERT_ALLOC_FIELDS = `
  id edu_student_id certificate_type_id certificate_type certificate_no
  issued_date certificate_file status student_name roll_no admission_no
  gender dob father_name class_id class_name section_id section_name
  academic_year academic_year_id branch_id institute_id
`;

const PURPLE = "#6366f1";

const STREAM_CLASS_IDS = [15, 16];
const isStreamClass = (classId) => STREAM_CLASS_IDS.includes(parseInt(classId));

const GET_STREAMS = gql`
    query { getStreams { id stream } }
`;

const GET_CLASSES = gql`
    query($id: Int) {
        getExamClasses(id: $id) {
            id
            class
        }
    }
`;

const GET_STUDENT_DETAILS = gql`
    query($institute_id: Int) {
        getNewEducationalStudents(institute_id: $institute_id) {
            student_id
            student_name
            branch_id
            class_id
            section_id
        }
    }
`;

// ── Multi-select dropdown with checkboxes ─────────────────────────────────────
function MultiSelect({ label, icon, options, selected, onChange, onToggleOpen, isOpen, idKey = 'id', labelKey = 'label' }) {
    const containerRef = useRef(null);
    const [openAbove, setOpenAbove] = useState(false);

    useEffect(() => {
        if (isOpen && containerRef.current) {
            const rect = containerRef.current.getBoundingClientRect();
            const spaceBelow = window.innerHeight - rect.bottom;
            const spaceAbove = rect.top;
            setOpenAbove(spaceAbove > spaceBelow && spaceBelow < 240);
        }
    }, [isOpen]);

    const allSelected  = options.length > 0 && selected.length === options.length;
    const noneSelected = selected.length === 0;
    const toggleAll    = () => onChange(allSelected ? [] : options.map(o => String(o[idKey])));
    const toggleOne    = (id) => { const sid = String(id); onChange(selected.includes(sid) ? selected.filter(s => s !== sid) : [...selected, sid]); };
    const displayLabel = (noneSelected || allSelected) ? `All ${label}s` : `${selected.length} selected`;
    const dropdownPos  = openAbove
        ? { bottom: '100%', top: 'auto', marginTop: 0, marginBottom: 4 }
        : { top: '100%', bottom: 'auto', marginTop: 4, marginBottom: 0 };

    return (
        <div ref={containerRef} style={{ position: 'relative', zIndex: isOpen ? 200 : 1 }}>
            <div style={ms.label}>{icon && <i className={icon} style={{ marginRight: 5 }} />}{label}</div>
            <button type="button" onClick={onToggleOpen}
                style={{ ...ms.trigger, borderColor: isOpen ? PURPLE : '#d1d5db', boxShadow: isOpen ? `0 0 0 3px ${PURPLE}22` : 'none' }}>
                <span style={{ flex: 1, textAlign: 'left', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', color: '#1f2937' }}>{displayLabel}</span>
                {!noneSelected && !allSelected && (
                    <span style={{ background: PURPLE, color: '#fff', borderRadius: '50%', width: 20, height: 20, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 11, fontWeight: 700, flexShrink: 0 }}>{selected.length}</span>
                )}
                <i className={`fal fa-chevron-${isOpen ? 'up' : 'down'}`} style={{ fontSize: 11, color: '#6b7280', flexShrink: 0 }} />
            </button>
            {isOpen && (
                <div style={{ ...ms.dropdown, ...dropdownPos }}>
                    <label style={ms.checkRow}>
                        <input type="checkbox" checked={allSelected} onChange={toggleAll} style={ms.checkbox} />
                        <span style={{ ...ms.checkLabel, fontWeight: 700, color: '#374151' }}>Select All</span>
                    </label>
                    <div style={{ height: 1, background: '#e5e7eb', margin: '2px 0' }} />
                    <div style={{ maxHeight: 200, overflowY: 'auto' }}>
                        {options.length === 0
                            ? <div style={{ padding: '10px 14px', fontSize: 12, color: '#9ca3af' }}>No options</div>
                            : options.map(opt => {
                                const sid = String(opt[idKey]); const checked = selected.includes(sid);
                                return (
                                    <label key={sid} style={{ ...ms.checkRow, background: checked ? `${PURPLE}08` : 'transparent' }}>
                                        <input type="checkbox" checked={checked} onChange={() => toggleOne(opt[idKey])} style={ms.checkbox} />
                                        <span style={{ ...ms.checkLabel, color: checked ? PURPLE : '#374151', fontWeight: checked ? 600 : 400 }}>{opt[labelKey]}</span>
                                    </label>
                                );
                            })
                        }
                    </div>
                </div>
            )}
        </div>
    );
}

const ms = {
    label:     { fontSize: 11, fontWeight: 700, color: '#6b7280', textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: 5 },
    trigger:   { width: '100%', display: 'flex', alignItems: 'center', gap: 8, background: '#fff', border: '1.5px solid #d1d5db', borderRadius: 8, padding: '8px 12px', fontSize: 13, cursor: 'pointer', transition: 'all 0.15s' },
    dropdown:  { position: 'absolute', top: '100%', left: 0, right: 0, zIndex: 1000, background: '#fff', border: '1.5px solid #e2e8f0', borderRadius: 10, boxShadow: '0 8px 24px rgba(0,0,0,0.12)', marginTop: 4, overflow: 'hidden' },
    checkRow:  { display: 'flex', alignItems: 'center', gap: 10, padding: '8px 12px', cursor: 'pointer', margin: 0, width: '100%' },
    checkbox:  { width: 16, height: 16, accentColor: PURPLE, cursor: 'pointer', flexShrink: 0 },
    checkLabel:{ fontSize: 13, userSelect: 'none' },
};

// ── Certificate image lightbox ────────────────────────────────────────────────
function CertLightbox({ url, onClose }) {
    const areaRef              = useRef(null);
    const [natW,    setNatW]   = useState(0);
    const [natH,    setNatH]   = useState(0);
    const [zoom,    setZoom]   = useState(0.5);
    const [fitZoom, setFitZoom] = useState(1);
    const loaded = natW > 0;

    useEffect(() => {
        const onKey = (e) => {
            if (e.key === 'Escape') onClose();
            if ((e.key === '+' || e.key === '=') && !e.ctrlKey) setZoom(z => +Math.min(z + 0.25, 4).toFixed(2));
            if (e.key === '-'                    && !e.ctrlKey) setZoom(z => +Math.max(z - 0.25, 0.1).toFixed(2));
        };
        document.addEventListener('keydown', onKey);
        return () => document.removeEventListener('keydown', onKey);
    }, [onClose]);

    const handleLoad = (e) => {
        const nW = e.target.naturalWidth  || 794;
        const nH = e.target.naturalHeight || 1123;
        setNatW(nW);
        setNatH(nH);
        if (areaRef.current) {
            const aW  = areaRef.current.clientWidth  - 48;
            const aH  = areaRef.current.clientHeight - 48;
            const fit = +Math.min(1, aW / nW, aH / nH).toFixed(3);
            setFitZoom(fit);
            setZoom(0.5);
        }
    };

    const zoomIn   = () => setZoom(z => +Math.min(z + 0.25, 4).toFixed(2));
    const zoomOut  = () => setZoom(z => +Math.max(z - 0.25, 0.1).toFixed(2));
    const doFit    = () => setZoom(fitZoom);
    const doFull   = () => setZoom(1);

    const handleDownload = async () => {
        try {
            const res  = await fetch(url, { mode: 'cors' });
            const blob = await res.blob();
            const ext  = (blob.type || 'image/png').split('/')[1]?.split('+')[0] || 'png';
            const a    = document.createElement('a');
            a.href     = URL.createObjectURL(blob);
            a.download = `certificate.${ext}`;
            a.click();
            setTimeout(() => URL.revokeObjectURL(a.href), 10000);
        } catch {
            window.open(url, '_blank');
        }
    };

    const handlePrint = () => {
        // Print via a hidden iframe so no new browser tab/window is opened.
        const iframe = document.createElement('iframe');
        iframe.setAttribute('aria-hidden', 'true');
        iframe.style.cssText = 'position:fixed;right:0;bottom:0;width:0;height:0;border:0;';
        document.body.appendChild(iframe);

        const cleanup = () => { setTimeout(() => { try { document.body.removeChild(iframe); } catch (e) {} }, 1000); };

        const doc = iframe.contentWindow.document;
        doc.open();
        doc.write(`<!DOCTYPE html><html><head><title>Certificate</title>
            <style>
                @page { size: auto; margin: 10mm; }
                * { box-sizing: border-box; margin: 0; padding: 0; -webkit-print-color-adjust: exact !important; print-color-adjust: exact !important; }
                /* Top-align (no flex centering / 100vh) so the certificate prints at the top, not the middle. */
                html, body { background: #fff; }
                img { width: 100%; height: auto; display: block; }
            </style></head>
            <body><img src="${url}" /></body></html>`);
        doc.close();

        const triggerPrint = () => {
            try { iframe.contentWindow.focus(); iframe.contentWindow.print(); } catch (e) {}
            cleanup();
        };

        const imgEl = doc.querySelector('img');
        if (imgEl && !imgEl.complete) {
            imgEl.onload = triggerPrint;
            imgEl.onerror = triggerPrint;
        } else {
            setTimeout(triggerPrint, 300);
        }
    };

    const pct        = Math.round(zoom * 100);
    const isOverflow = loaded && zoom > fitZoom + 0.01;
    const imgW = loaded ? natW * zoom : 'auto';
    const imgH = loaded ? natH * zoom : 'auto';

    const iconBtn = (bg = 'rgba(255,255,255,0.12)', extra = {}) => ({
        display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
        gap: 5, background: bg, color: '#fff', border: 'none',
        borderRadius: 8, padding: '7px 14px', fontWeight: 700,
        fontSize: 13, cursor: 'pointer', transition: 'background 0.15s',
        ...extra,
    });

    return (
        <div
            onClick={onClose}
            style={{
                position: 'fixed', inset: 0, zIndex: 99999,
                background: 'rgba(10,10,18,0.92)',
                display: 'flex', flexDirection: 'column',
                backdropFilter: 'blur(4px)',
            }}
        >
            <div
                onClick={e => e.stopPropagation()}
                style={{
                    display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                    flexWrap: 'wrap', gap: 10,
                    padding: '10px 20px', flexShrink: 0,
                    background: 'rgba(255,255,255,0.05)',
                    borderBottom: '1px solid rgba(255,255,255,0.09)',
                }}
            >
                <span style={{ color: '#fff', fontWeight: 700, fontSize: 15, display: 'flex', alignItems: 'center', gap: 8 }}>
                    <i className="fal fa-file-certificate" style={{ color: '#a5b4fc' }} />
                    Certificate Preview
                </span>
                <div style={{
                    display: 'flex', alignItems: 'center', gap: 4,
                    background: 'rgba(255,255,255,0.08)',
                    border: '1px solid rgba(255,255,255,0.14)',
                    borderRadius: 10, padding: '4px 8px',
                }}>
                    <button onClick={zoomOut} title="Zoom out  [−]" style={{ ...iconBtn(), padding: '5px 10px', fontSize: 16, lineHeight: 1 }}>−</button>
                    <span style={{ color: '#e5e7eb', fontSize: 13, fontWeight: 700, minWidth: 48, textAlign: 'center', userSelect: 'none' }}>{pct}%</span>
                    <button onClick={zoomIn} title="Zoom in  [+]" style={{ ...iconBtn(), padding: '5px 10px', fontSize: 16, lineHeight: 1 }}>+</button>
                    <div style={{ width: 1, height: 20, background: 'rgba(255,255,255,0.2)', margin: '0 6px' }} />
                    <button onClick={doFit} title="Fit to screen" style={{ ...iconBtn('rgba(99,102,241,0.3)'), padding: '5px 10px', fontSize: 12 }}>
                        <i className="fal fa-compress-alt" /> Fit
                    </button>
                    <button onClick={doFull} title="Actual size (100%)" style={{ ...iconBtn('rgba(255,255,255,0.1)'), padding: '5px 10px', fontSize: 12 }}>1:1</button>
                </div>
                <div style={{ display: 'flex', gap: 8 }}>
                    <button onClick={handlePrint} style={iconBtn('rgba(255,255,255,0.15)')}><i className="fal fa-print" /> Print</button>
                    <button onClick={handleDownload} style={iconBtn('#22c55e')}><i className="fal fa-download" /> Download</button>
                    <button onClick={onClose} style={iconBtn('#ef4444')}><i className="fal fa-times" /> Close</button>
                </div>
            </div>
            <div
                ref={areaRef}
                onClick={e => e.stopPropagation()}
                style={{
                    flex: 1,
                    overflow: isOverflow ? 'auto' : 'hidden',
                    display: 'flex',
                    alignItems: isOverflow ? 'flex-start' : 'center',
                    justifyContent: isOverflow ? 'flex-start' : 'center',
                    padding: 24,
                }}
            >
                <div style={{ display: 'inline-flex', alignItems: 'flex-start', justifyContent: 'center', minWidth: isOverflow ? '100%' : 'auto', minHeight: isOverflow ? '100%' : 'auto' }}>
                    <img
                        src={url}
                        alt="Certificate"
                        onLoad={handleLoad}
                        style={{
                            display: 'block',
                            width: imgW, height: imgH,
                            maxWidth:  loaded ? 'none' : 'calc(100vw - 80px)',
                            maxHeight: loaded ? 'none' : 'calc(100vh - 100px)',
                            borderRadius: 8,
                            boxShadow: '0 8px 48px rgba(0,0,0,0.55)',
                            background: '#fff',
                            transition: 'width 0.12s ease, height 0.12s ease',
                        }}
                        onError={e => { e.target.style.display = 'none'; }}
                    />
                </div>
            </div>
            <div style={{ textAlign: 'center', padding: '8px 0', flexShrink: 0, fontSize: 11, color: 'rgba(255,255,255,0.3)', userSelect: 'none' }}>
                Scroll to pan when zoomed · Esc to close · + / − to zoom
            </div>
        </div>
    );
}

// ── Type badge colours ────────────────────────────────────────────────────────
const TYPE_COLORS = {
    'transfer certificate': { bg: '#fee2e2', color: '#dc2626' },
    'tc':                   { bg: '#fee2e2', color: '#dc2626' },
    'study certificate':    { bg: '#dcfce7', color: '#15803d' },
    'bonafide certificate': { bg: '#dbeafe', color: '#1d4ed8' },
    'conduct certificate':  { bg: '#fef9c3', color: '#92400e' },
};
const getBadgeStyle = (type = '') => TYPE_COLORS[type.toLowerCase()] || { bg: '#f3f4f6', color: '#374151' };

// ─────────────────────────────────────────────────────────────────────────────

class CertificateHistory extends Component {

    constructor(props) {
        super(props);
        this.state = {
            filterType:        'ALL',
            searchText:        '',
            loading:           false,
            error:             null,

            selectedBranches:  [],
            selectedCertTypes: [],
            selectedClasses:   [],
            selectedStreams:   [],
            selectedSections:  [],
            selectedStudents:  [],
            openDropdown:      null,

            certificateHistory: [],
            allClasses:        [],
            allStreams:        [],
            allStudents:       [],

            viewCertUrl:       null,
        };
        this.filterRef = React.createRef();
    }

    componentDidMount() {
        document.addEventListener('mousedown', this.handleOutsideClick);
        this.fetchClasses();
        this.fetchStreams();
        this.fetchStudents();
        this.fetchHistory();
        this.syncDefaultSelections(this.props);
    }

    componentDidUpdate(prevProps) {
        const branchesArrived  = prevProps.branches.length        === 0 && this.props.branches.length        > 0;
        const certTypesArrived = prevProps.certificateTypes.length === 0 && this.props.certificateTypes.length > 0;
        if (branchesArrived || certTypesArrived) this.syncDefaultSelections(this.props);
    }

    componentWillUnmount() {
        document.removeEventListener('mousedown', this.handleOutsideClick);
    }

    syncDefaultSelections = (props) => {
        const update = {};
        if (props.branches.length > 0 && this.state.selectedBranches.length === 0) {
            update.selectedBranches  = props.branches.map(b => String(b.id));
            update.selectedClasses   = Array.from(new Set(props.branches.flatMap(b => b.sections || []).map(s => String(s.class_id))));
            update.selectedSections  = props.branches.flatMap(b => b.sections || []).map(s => String(s.id));
            update.selectedStreams   = Array.from(new Set(
                props.branches.flatMap(b => b.sections || [])
                    .filter(s => isStreamClass(s.class_id) && s.stream_id != null)
                    .map(s => String(s.stream_id))
            ));
        }
        if (props.certificateTypes.length > 0 && this.state.selectedCertTypes.length === 0)
            update.selectedCertTypes = props.certificateTypes.map(ct => String(ct.id));
        if (Object.keys(update).length > 0) this.setState(update);
    };

    handleOutsideClick = (e) => {
        if (this.filterRef.current && !this.filterRef.current.contains(e.target))
            this.setState({ openDropdown: null });
    };

    fetchClasses = async () => {
        try {
            const result = await this.props.client.query({
                query: GET_CLASSES,
                variables: { id: 1 },
                fetchPolicy: 'network-only',
            });
            this.setState({ allClasses: result.data.getExamClasses || [] });
        } catch { /* ignore */ }
    };

    fetchStreams = async () => {
        try {
            const result = await this.props.client.query({
                query: GET_STREAMS,
                fetchPolicy: 'network-only',
            });
            this.setState({ allStreams: result.data.getStreams || [] });
        } catch { /* ignore */ }
    };

    fetchStudents = async () => {
        try {
            const result = await this.props.client.query({
                query: GET_STUDENT_DETAILS,
                variables: { institute_id: parseInt(Cookies.get('institutionid')) },
                fetchPolicy: 'network-only',
            });
            const students = result.data.getNewEducationalStudents || [];
            this.setState({
                allStudents:      students,
                selectedStudents: students.map(s => String(s.student_id)),
            });
        } catch { /* ignore */ }
    };

    fetchHistory = async () => {
        this.setState({ loading: true, error: null, openDropdown: null });
        try {
            const instituteId = parseInt(Cookies.get('institutionid'));
            const query = gql(`{
                getCertificateAllocations(institute_id: ${instituteId}) {
                    ${CERT_ALLOC_FIELDS}
                }
            }`);
            const result = await this.props.client.query({ query, fetchPolicy: 'network-only' });
            this.setState({
                certificateHistory: result.data.getCertificateAllocations || [],
                loading: false, filterType: 'ALL',
            });
        } catch (err) {
            console.error('[CertHistory]', err);
            this.setState({ loading: false, error: err.message || 'Failed to load data' });
        }
    };

    getFilteredData = () => {
        const {
            filterType, searchText, certificateHistory,
            selectedBranches, selectedCertTypes,
            selectedClasses, selectedStreams, selectedSections, selectedStudents,
        } = this.state;
        const { branches = [], certificateTypes = [] } = this.props;

        let data = certificateHistory;

        if (selectedBranches.length > 0 && selectedBranches.length < branches.length)
            data = data.filter(i => selectedBranches.includes(String(i.branch_id)));

        if (selectedCertTypes.length > 0 && selectedCertTypes.length < certificateTypes.length)
            data = data.filter(i => selectedCertTypes.includes(String(i.certificate_type_id)));

        if (selectedClasses.length > 0)
            data = data.filter(i => selectedClasses.includes(String(i.class_id)));

        // Stream filter — narrow stream-class records (15/16) by their section's stream.
        // Empty selectedStreams ⇒ treat as all (no narrowing).
        if (selectedStreams.length > 0) {
            const sectionStreamMap = {};
            branches.flatMap(b => b.sections || []).forEach(s => {
                if (s.stream_id != null) sectionStreamMap[String(s.id)] = String(s.stream_id);
            });
            data = data.filter(i => {
                if (!isStreamClass(i.class_id)) return true;
                const streamId = sectionStreamMap[String(i.section_id)];
                return streamId == null || selectedStreams.includes(streamId);
            });
        }

        if (selectedSections.length > 0)
            data = data.filter(i => selectedSections.includes(String(i.section_id)));

        const { allStudents } = this.state;
        if (selectedStudents.length > 0 && selectedStudents.length < allStudents.length)
            data = data.filter(i => selectedStudents.includes(String(i.edu_student_id)));

        if (filterType !== 'ALL')
            data = data.filter(i => (i.certificate_type || '').toLowerCase() === filterType.toLowerCase());

        if (searchText.trim()) {
            const q = searchText.toLowerCase();
            data = data.filter(i =>
                (i.student_name    || '').toLowerCase().includes(q) ||
                (i.certificate_no  || '').toLowerCase().includes(q) ||
                (i.certificate_type|| '').toLowerCase().includes(q) ||
                (i.admission_no    || '').toLowerCase().includes(q) ||
                (i.father_name     || '').toLowerCase().includes(q) ||
                (i.roll_no         || '').toLowerCase().includes(q)
            );
        }

        return data;
    };

    formatDate = (dateStr) => {
        if (!dateStr) return '—';
        try {
            const s = String(dateStr);
            const d = /^\d+$/.test(s)
                ? new Date(s.length >= 13 ? +s : +s * 1000)
                : new Date(s);
            return isNaN(d.getTime()) ? s : d.toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' });
        } catch { return String(dateStr); }
    };

    tableData = (filteredData, branchMap) => {
        return filteredData.map((item, index) => ({
            ...item,
            sno:                    index + 1,
            branch_name:            branchMap[String(item.branch_id)] || '—',
            issued_date_formatted:  this.formatDate(item.issued_date),
        }));
    };

    render() {
        const {
            filterType, searchText, loading, error,
            selectedBranches, selectedCertTypes,
            selectedClasses, selectedStreams, selectedSections, selectedStudents,
            openDropdown,
            allClasses, allStreams, allStudents,
            viewCertUrl,
        } = this.state;
        const { onBack, branches = [], certificateTypes = [] } = this.props;

        const certificateHistory = this.state.certificateHistory;

        // ── Filter options ────────────────────────────────────────────────────
        const selectedBranchObjs = selectedBranches.length > 0
            ? branches.filter(b => selectedBranches.includes(String(b.id)))
            : branches;

        const classIdSet = new Set(selectedBranchObjs.flatMap(b => b.sections || []).map(s => String(s.class_id)));
        const classOptions = allClasses
            .filter(c => classIdSet.has(String(c.id)))
            .map(c => ({ id: c.id, label: c.class }));

        // Stream options — distinct stream_ids among selected branches' stream-class
        // sections whose class is in selectedClasses, labelled via allStreams.
        const streamIdSet = new Set(
            selectedBranchObjs
                .flatMap(b => b.sections || [])
                .filter(s => isStreamClass(s.class_id) && s.stream_id != null
                    && (selectedClasses.length === 0 || selectedClasses.includes(String(s.class_id))))
                .map(s => String(s.stream_id))
        );
        const streamOptions = allStreams
            .filter(st => streamIdSet.has(String(st.id)))
            .map(st => ({ id: st.id, label: st.stream }));
        const showStream = streamOptions.length > 0;

        const sectionOptions = selectedBranchObjs
            .flatMap(b => b.sections || [])
            .filter(s => selectedClasses.length === 0 || selectedClasses.includes(String(s.class_id)))
            .filter(s => !isStreamClass(s.class_id) || selectedStreams.length === 0 || (s.stream_id != null && selectedStreams.includes(String(s.stream_id))))
            .map(s => ({ id: s.id, label: s.section_name }));

        const studentOptions = allStudents
            .filter(s => selectedBranches.length === 0 || selectedBranches.includes(String(s.branch_id)))
            .map(s => ({ id: s.student_id, label: s.student_name }));

        // ── Counts / types ────────────────────────────────────────────────────
        const totalCount  = certificateHistory.length;
        const typeCounts  = certificateHistory.reduce((acc, i) => { const t = i.certificate_type || 'OTHER'; acc[t] = (acc[t] || 0) + 1; return acc; }, {});
        const uniqueTypes = [...new Set(certificateHistory.map(i => i.certificate_type).filter(Boolean))];

        const branchOptions   = branches.map(b  => ({ id: b.id,  label: b.branch_name }));
        const certTypeOptions = certificateTypes.map(ct => ({ id: ct.id, label: ct.certificate_type }));

        const filteredData = this.getFilteredData();
        const branchMap    = Object.fromEntries(branches.map(b => [String(b.id), b.branch_name]));
        const tableRows    = this.tableData(filteredData, branchMap);

        const anyFilterActive = (selectedBranches.length > 0 && selectedBranches.length < branches.length)
            || (selectedCertTypes.length > 0 && selectedCertTypes.length < certificateTypes.length)
            || (selectedClasses.length > 0 && selectedClasses.length < classOptions.length)
            || (selectedStreams.length > 0 && selectedStreams.length < streamOptions.length)
            || (selectedSections.length > 0 && selectedSections.length < sectionOptions.length)
            || (selectedStudents.length < allStudents.length);

        const resetFilters = () => {
            const allClassIds   = Array.from(new Set(branches.flatMap(b => b.sections || []).map(s => String(s.class_id))));
            const allSectionIds = branches.flatMap(b => b.sections || []).map(s => String(s.id));
            const allStreamIds  = Array.from(new Set(branches.flatMap(b => b.sections || []).filter(s => isStreamClass(s.class_id) && s.stream_id != null).map(s => String(s.stream_id))));
            this.setState({
                selectedBranches:  branches.map(b => String(b.id)),
                selectedCertTypes: certificateTypes.map(ct => String(ct.id)),
                selectedClasses:   allClassIds,
                selectedStreams:   allStreamIds,
                selectedSections:  allSectionIds,
                selectedStudents:  allStudents.map(s => String(s.student_id)),
                filterType:        'ALL',
            });
        };

        // ── Column definitions ────────────────────────────────────────────────
        const certColumns = [
            {
                dataField: 'sno',
                text: '#',
                formatter: (_, __, idx) => idx + 1,
            },
            {
                dataField: 'certificate_no',
                text: 'Certificate No',
                sort: true,
                formatter: (cell) => <span className="dh-adm-badge" style={{ whiteSpace: 'nowrap', display: 'inline-block' }}>{cell || '—'}</span>,
            },
            {
                dataField: 'certificate_type',
                text: 'Certificate Type',
                sort: true,
                formatter: (cell) => {
                    const badge = getBadgeStyle(cell);
                    return (
                        <span style={{ background: badge.bg, color: badge.color, padding: '4px 12px', borderRadius: 20, fontWeight: 700, fontSize: 12, whiteSpace: 'nowrap' }}>
                            {cell || '—'}
                        </span>
                    );
                },
            },
            {
                dataField: 'student_name',
                text: 'Student Name',
                sort: true,
                formatter: (cell) => <span style={{ fontWeight: 600, textTransform: 'uppercase', fontSize: 11, whiteSpace: 'nowrap' }}>{cell || '—'}</span>,
            },
            {
                dataField: 'father_name',
                text: 'Father Name',
                sort: true,
                formatter: (cell) => cell || '—',
            },
            {
                dataField: 'admission_no',
                text: 'Admission No',
                sort: true,
                formatter: (cell) => cell || '—',
            },
            {
                dataField: 'branch_name',
                text: 'Branch',
                sort: true,
                formatter: (cell) => cell || '—',
            },
            {
                dataField: 'class_name',
                text: 'Class',
                sort: true,
                formatter: (cell) => <span style={{ textTransform: 'uppercase', fontWeight: 500, fontSize: 11, whiteSpace: 'nowrap' }}>{cell || '—'}</span>,
            },
            {
                dataField: 'section_name',
                text: 'Section',
                sort: true,
                formatter: (cell) => <span style={{ fontSize: 11, whiteSpace: 'nowrap' }}>{cell || '—'}</span>,
            },
            {
                dataField: 'academic_year',
                text: 'Academic Year',
                sort: true,
                formatter: (cell) => cell || '—',
            },
            {
                dataField: 'issued_date_formatted',
                text: 'Issue Date',
                sort: true,
                formatter: (cell) => <span style={{ fontSize: 11, whiteSpace: 'nowrap' }}>{cell || '—'}</span>,
            },
            {
                dataField: 'actions',
                text: 'Action',
                classes: 'dh-sticky-actions',
                headerClasses: 'dh-sticky-actions',
                formatter: (_, row) => row.certificate_file ? (
                    <div className="dh-action-wrap">
                        <button
                            className="dh-view-btn"
                            onClick={() => this.setState({ viewCertUrl: row.certificate_file })}
                        >
                            <i className="fal fa-eye" /> View
                        </button>
                    </div>
                ) : (
                    <span style={{ color: '#9ca3af', fontSize: 12 }}>—</span>
                ),
            },
        ];

        const inner = (
            <div>

                {/* PAGE HEADER */}
                {!this.props.hideHeader && (
                    <div style={S.pageHeader}>
                        <button style={S.backBtn} onClick={onBack}>&#8592; Back</button>
                        <div style={S.pageTitle}>Certificate History</div>
                    </div>
                )}

                {/* KPI CARDS */}
                {!loading && !error && totalCount > 0 && (
                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(220px, 1fr))', gap: 16, margin: '24px 24px 0' }}>
                        {[
                            { label: 'Total Certificates', value: totalCount, color: '#6366f1', icon: 'fal fa-certificate', sub: 'All records' },
                            ...uniqueTypes.map((type, i) => {
                                // Pick colour/icon by the certificate type itself (not by position),
                                // and render a card for EVERY type — not just the first three.
                                const norm = String(type).toUpperCase().replace(/[\s_-]/g, '');
                                const TYPE_CARD_STYLE = {
                                    IDCARD:      { color: '#c2185b', icon: 'fal fa-id-card' },
                                    STUDY:       { color: '#10b981', icon: 'fal fa-graduation-cap' },
                                    RECORDSHEET: { color: '#f59e0b', icon: 'fal fa-table' },
                                    TC:          { color: '#ef4444', icon: 'fal fa-file-alt' },
                                    BONAFIDE:    { color: '#B8860B', icon: 'fal fa-award' },
                                };
                                const fallbackPalette = ['#6366f1', '#0891b2', '#7c3aed', '#0ea5e9', '#db2777'];
                                const style = TYPE_CARD_STYLE[norm]
                                    || (norm.includes('TRANSFER') ? TYPE_CARD_STYLE.TC : null)
                                    || (norm.includes('RECORD')   ? TYPE_CARD_STYLE.RECORDSHEET : null)
                                    || { color: fallbackPalette[i % fallbackPalette.length], icon: 'fal fa-file-certificate' };
                                return {
                                    label: type, value: typeCounts[type] || 0,
                                    color: style.color,
                                    icon:  style.icon,
                                    sub:   `${type} issued`,
                                };
                            }),
                        ].map(({ label, value, color, icon, sub }) => (
                            <div key={label} style={{ background: '#fff', border: '1px solid #e5e7eb', borderTop: `4px solid ${color}`, borderRadius: 14, padding: '16px 18px', boxShadow: '0 2px 8px rgba(0,0,0,0.04)', display: 'flex', alignItems: 'center', gap: 14 }}>
                                <div style={{ background: `${color}15`, color, borderRadius: 10, width: 46, height: 46, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                                    <i className={icon} style={{ fontSize: 20 }} />
                                </div>
                                <div>
                                    <div style={{ fontSize: 10, color: '#6b7280', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.5px' }}>{label}</div>
                                    <div style={{ fontSize: 24, fontWeight: 800, color: '#1f2937', lineHeight: 1.2 }}>{value}</div>
                                    <div style={{ fontSize: 11, color: '#9ca3af' }}>{sub}</div>
                                </div>
                            </div>
                        ))}
                    </div>
                )}

                {/* ERROR BANNER */}
                {error && (
                    <div style={{ background: '#fef2f2', border: '1px solid #fecaca', borderRadius: 8, padding: '10px 16px', margin: '16px 24px 0', color: '#dc2626', fontSize: 13, display: 'flex', alignItems: 'center', gap: 8 }}>
                        <i className="fal fa-exclamation-circle" />
                        <span><strong>Error:</strong> {error}</span>
                        <button onClick={this.fetchHistory} style={{ marginLeft: 'auto', background: 'none', border: '1px solid #dc2626', borderRadius: 6, color: '#dc2626', padding: '3px 10px', cursor: 'pointer', fontSize: 12 }}>Retry</button>
                    </div>
                )}

                {/* ── TABLE CARD ── */}
                <div style={{ padding: '24px' }}>
                <Card className="dh-table-card">

                    {/* Card Header */}
                    <div className="dh-table-header">
                        <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
                            <div style={{ background: `${PURPLE}15`, color: PURPLE, borderRadius: 8, width: 30, height: 30, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                <i className="fal fa-sliders-h" style={{ fontSize: 13 }} />
                            </div>
                            <span style={{ fontWeight: 700, fontSize: 13, color: '#1f2937' }}>Filters</span>
                            {anyFilterActive && (
                                <button onClick={resetFilters}
                                    style={{ fontSize: 12, color: '#6b7280', background: 'none', border: '1px solid #e5e7eb', borderRadius: 6, padding: '3px 10px', cursor: 'pointer' }}>
                                    Reset filters
                                </button>
                            )}
                            <button onClick={this.fetchHistory} disabled={loading} style={S.refreshBtn}>
                                <i className={`fal fa-${loading ? 'spinner fa-spin' : 'sync-alt'}`} style={{ marginRight: 6 }} />
                                {loading ? 'Loading…' : 'Refresh'}
                            </button>
                        </div>
                        <span className="dh-record-badge">
                            {filteredData.length} {filteredData.length === 1 ? 'record' : 'records'}
                        </span>
                    </div>

                    {/* Filter bar */}
                    <div style={{ padding: '16px 20px', borderBottom: '1px solid #f1f5f9' }} ref={this.filterRef}>

                        {/* Row 1 — Branch | Class | Stream | Section | CertType */}
                        <div style={{ display: 'grid', gridTemplateColumns: showStream ? 'repeat(5, 1fr)' : 'repeat(4, 1fr)', gap: 14, marginBottom: 14 }}>

                            <MultiSelect
                                label="Branch" icon="fal fa-code-branch"
                                options={branchOptions} selected={selectedBranches}
                                onChange={val => {
                                    const selBranches   = branches.filter(b => val.includes(String(b.id)));
                                    const newClassIds   = Array.from(new Set(selBranches.flatMap(b => b.sections || []).map(s => String(s.class_id))));
                                    const newStreamIds  = Array.from(new Set(selBranches.flatMap(b => b.sections || []).filter(s => isStreamClass(s.class_id) && s.stream_id != null).map(s => String(s.stream_id))));
                                    const newSectionIds = selBranches.flatMap(b => b.sections || []).map(s => String(s.id));
                                    this.setState({ selectedBranches: val, selectedClasses: newClassIds, selectedStreams: newStreamIds, selectedSections: newSectionIds, selectedStudents: allStudents.map(s => String(s.student_id)) });
                                }}
                                onToggleOpen={() => this.setState({ openDropdown: openDropdown === 'branch' ? null : 'branch' })}
                                isOpen={openDropdown === 'branch'}
                            />

                            <MultiSelect
                                label="Class" icon="fal fa-layer-group"
                                options={classOptions} selected={selectedClasses}
                                onChange={val => {
                                    const selBranches   = selectedBranches.length > 0 ? branches.filter(b => selectedBranches.includes(String(b.id))) : branches;
                                    const newStreamIds  = Array.from(new Set(selBranches.flatMap(b => b.sections || [])
                                        .filter(s => isStreamClass(s.class_id) && s.stream_id != null && val.includes(String(s.class_id)))
                                        .map(s => String(s.stream_id))));
                                    const newSectionIds = selBranches.flatMap(b => b.sections || [])
                                        .filter(s => val.includes(String(s.class_id)))
                                        .map(s => String(s.id));
                                    this.setState({ selectedClasses: val, selectedStreams: newStreamIds, selectedSections: newSectionIds, selectedStudents: allStudents.map(s => String(s.student_id)) });
                                }}
                                onToggleOpen={() => this.setState({ openDropdown: openDropdown === 'class' ? null : 'class' })}
                                isOpen={openDropdown === 'class'}
                            />

                            {showStream && (
                                <MultiSelect
                                    label="Stream" icon="fal fa-stream"
                                    options={streamOptions} selected={selectedStreams}
                                    onChange={val => {
                                        const selBranches   = selectedBranches.length > 0 ? branches.filter(b => selectedBranches.includes(String(b.id))) : branches;
                                        // Narrow sections: keep non-stream-class sections + stream-class sections whose stream is selected.
                                        const newSectionIds = selBranches.flatMap(b => b.sections || [])
                                            .filter(s => selectedClasses.length === 0 || selectedClasses.includes(String(s.class_id)))
                                            .filter(s => !isStreamClass(s.class_id) || (s.stream_id != null && val.includes(String(s.stream_id))))
                                            .map(s => String(s.id));
                                        this.setState({ selectedStreams: val, selectedSections: newSectionIds, selectedStudents: allStudents.map(s => String(s.student_id)) });
                                    }}
                                    onToggleOpen={() => this.setState({ openDropdown: openDropdown === 'stream' ? null : 'stream' })}
                                    isOpen={openDropdown === 'stream'}
                                />
                            )}

                            <MultiSelect
                                label="Section" icon="fal fa-users"
                                options={sectionOptions} selected={selectedSections}
                                onChange={val => this.setState({ selectedSections: val, selectedStudents: allStudents.map(s => String(s.student_id)) })}
                                onToggleOpen={() => this.setState({ openDropdown: openDropdown === 'section' ? null : 'section' })}
                                isOpen={openDropdown === 'section'}
                            />

                            {certTypeOptions.length > 0 ? (
                                <MultiSelect
                                    label="Certificate Type" icon="fal fa-certificate"
                                    options={certTypeOptions} selected={selectedCertTypes}
                                    onChange={v => this.setState({ selectedCertTypes: v })}
                                    onToggleOpen={() => this.setState({ openDropdown: openDropdown === 'certType' ? null : 'certType' })}
                                    isOpen={openDropdown === 'certType'}
                                />
                            ) : <div />}
                        </div>

                        {/* Row 2 — Student */}
                        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr 1fr', gap: 14 }}>
                            <MultiSelect
                                label="Student" icon="fal fa-user-graduate"
                                options={studentOptions} selected={selectedStudents}
                                onChange={v => this.setState({ selectedStudents: v })}
                                onToggleOpen={() => this.setState({ openDropdown: openDropdown === 'student' ? null : 'student' })}
                                isOpen={openDropdown === 'student'}
                            />
                            <div />
                        </div>
                    </div>

                    {/* Type tabs + Search row */}
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 12, padding: '12px 24px', borderBottom: '1px solid #f1f5f9' }}>
                        {/* Count tabs — left side */}
                        
                        {/* Search box — right side */}
                        <div style={{ position: 'relative' }}>
                            <i className="fal fa-search" style={{ position: 'absolute', left: 10, top: '50%', transform: 'translateY(-50%)', color: '#9ca3af', fontSize: 13, pointerEvents: 'none' }} />
                            <input
                                type="text"
                                placeholder="Search student, certificate…"
                                value={searchText}
                                onChange={e => this.setState({ searchText: e.target.value })}
                                style={{ paddingLeft: 32, paddingRight: searchText ? 32 : 12, paddingTop: 7, paddingBottom: 7, border: '1.5px solid #d1d5db', borderRadius: 8, fontSize: 13, color: '#1f2937', outline: 'none', width: 240, transition: 'border-color 0.15s' }}
                                onFocus={e => { e.target.style.borderColor = PURPLE; e.target.style.boxShadow = `0 0 0 3px ${PURPLE}22`; }}
                                onBlur={e => { e.target.style.borderColor = '#d1d5db'; e.target.style.boxShadow = 'none'; }}
                            />
                            {searchText && (
                                <button onClick={() => this.setState({ searchText: '' })}
                                    style={{ position: 'absolute', right: 8, top: '50%', transform: 'translateY(-50%)', background: 'none', border: 'none', cursor: 'pointer', color: '#9ca3af', fontSize: 14, lineHeight: 1, padding: 2 }}>
                                    ×
                                </button>
                            )}
                        </div>


                        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center' }}>
                            {!loading && !error && totalCount > 0
                                ? [{ key: 'ALL', label: 'All', count: totalCount }, ...uniqueTypes.map(t => ({ key: t, label: t, count: typeCounts[t] || 0 }))].map(({ key, label, count }) => {
                                    const active = filterType === key;
                                    return (
                                        <button key={key} onClick={() => this.setState({ filterType: key })}
                                            style={{ border: `1.5px solid ${active ? PURPLE : '#e5e7eb'}`, borderRadius: 20, padding: '5px 14px', background: active ? PURPLE : '#fff', color: active ? '#fff' : '#4b5563', fontWeight: 600, fontSize: 13, cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 6 }}>
                                            {label}
                                            <span style={{ background: active ? 'rgba(255,255,255,0.25)' : '#f3f4f6', color: active ? '#fff' : '#6b7280', borderRadius: 10, padding: '1px 7px', fontSize: 11, fontWeight: 700 }}>{count}</span>
                                        </button>
                                    );
                                })
                                : <span />
                            }
                        </div>
                    </div>   

                    {/* Table content */}
                    <div style={{ padding: '0px 24px' }}>
                        {loading ? (
                            <>
                                <style>{`
                                    @keyframes dh-shimmer {
                                        0%   { background-position: -800px 0; }
                                        100% { background-position:  800px 0; }
                                    }
                                    .dh-shimmer {
                                        background: linear-gradient(90deg, #f0f0f0 25%, #e0e0e0 50%, #f0f0f0 75%);
                                        background-size: 800px 100%;
                                        animation: dh-shimmer 1.4s infinite linear;
                                        border-radius: 5px;
                                    }
                                `}</style>
                                <div style={{ marginBottom: 14 }}>
                                    <div className="dh-shimmer" style={{ height: 34, width: 220, borderRadius: 8 }} />
                                </div>

                                
                                <div style={{ display: 'flex', gap: 10, padding: '10px 0', borderBottom: '2px solid #f3f4f6', marginBottom: 4 }}>
                                    {[40, 100, 130, 140, 100, 90, 80, 70, 70, 90, 80, 80].map((w, i) => (
                                        <div key={i} className="dh-shimmer" style={{ height: 13, width: w, flexShrink: 0 }} />
                                    ))}
                                </div>
                                {[1, 2, 3, 4, 5, 6, 7].map(row => (
                                    <div key={row} style={{ display: 'flex', gap: 10, padding: '13px 0', borderBottom: '1px solid #f3f4f6', alignItems: 'center' }}>
                                        {[40, 100, 130, 140, 100, 90, 80, 70, 70, 90, 80, 80].map((w, i) => (
                                            <div key={i} className="dh-shimmer" style={{ height: 12, width: w, flexShrink: 0 }} />
                                        ))}
                                    </div>
                                ))}
                            </>
                        ) : (
                            <CardLessDataTable
                                parentData={tableRows}
                                particlecolumns={certColumns}
                                searchBar={false}
                                name="certificate_history"
                            />
                        )}
                    </div>

                </Card>
                </div>

                {/* Certificate lightbox */}
                {viewCertUrl && (
                    <CertLightbox
                        url={viewCertUrl}
                        onClose={() => this.setState({ viewCertUrl: null })}
                    />
                )}

            </div>
        );

        if (this.props.hideHeader) return inner;

        return (
            <div style={{ background: 'linear-gradient(135deg, #f0f4ff 0%, #faf5ff 100%)', minHeight: '100vh', paddingBottom: 100 }}>
                <Container fluid className="px-0">
                    <div style={{ background: '#fff', borderRadius: 16, boxShadow: '0 4px 24px rgba(0,0,0,0.05)', overflow: 'hidden', border: '1px solid #e2e8f0' }}>
                        {inner}
                    </div>
                </Container>
            </div>
        );
    }
}

const S = {
    pageHeader: { background: 'linear-gradient(135deg, #6366f1, #8b5cf6)', padding: '18px 24px', color: '#fff', display: 'flex', alignItems: 'center', gap: 16 },
    backBtn:    { background: 'rgba(255,255,255,0.18)', border: '1px solid rgba(255,255,255,0.5)', color: '#fff', padding: '8px 18px', borderRadius: 8, cursor: 'pointer', fontWeight: 600, fontSize: 14 },
    pageTitle:  { fontSize: 20, fontWeight: 700 },
    refreshBtn: { marginLeft: 'auto', background: `${PURPLE}12`, color: PURPLE, border: `1px solid ${PURPLE}30`, borderRadius: 7, padding: '6px 14px', fontSize: 12, fontWeight: 600, cursor: 'pointer', display: 'flex', alignItems: 'center' },
};

export default withApollo(CertificateHistory);
